import React from 'react';
import { connect } from 'react-redux';
import { FetchModels, UploadPhotoToken } from 'features/reviews';
import { RootReducer } from 'store/root-reducer';
import { getAvailabledRegions, getAvailabledStores } from 'features/bisiness-units/selectors';
import { getDisplayedName, getPrimaryPrice, getProductId, getProductImages } from 'features/product-info';
import {
  getPostReviewErrors,
  getPostReviewIsPerforming,
  getPostReviewResultStatus,
} from 'features/reviews/selectors/post-reviews';
import { Dispatch } from 'redux';
import { POST_REVIEW } from 'features/reviews/actions/post-reviews';
import {
  PERFORM_UPLOAD_RETRY,
  PROCESS_SELECTED_PHOTOS,
  REMOVE_SELECTED_PHOTO,
} from 'features/reviews/actions/upload-photos';
import { getSelectedPhotos } from 'features/reviews/selectors/upload-photos';
import { mapFromStateToPostData, uploadTokenSelector } from './utils';
import { FormSubmitThanksSection } from '../content-blocks/form-submit-thanks';
import { Reducers } from '../state';
import { ReviewFormStatefull } from '../review-form-statefull';
import { Step } from '../types';
import {
  PDP_REVIEW_FORM_STEP_CHANGED,
  PDP_REVIEW_FORM_PUBLICATION_ERROR_SHOWN,
  ReviewFormActions,
} from '../actions';
import { ReviewFormConnectedProps, ReviewFormConnectedStateProps } from './types';
import { ProductCardWrapper } from './product-card-wrapper';

export const ReviewFormConnected: React.FC<ReviewFormConnectedProps> = ({
  breakpoint,
  productTitle,
  productPrice,
  productMainPhoto,
  productId,
  postReviewIsPerforming,
  postReviewResultStatus,
  selectedPhotos,
  onSubmit,
  onRetryPhotoUpload,
  onRemovePhoto,
  onPhotoSelectionClick,
  onPhotosSelect,
  onStepChanged,
  onReviewPublicationErrorShown,
  ...restProps
}) => {
  React.useEffect(() => {
    onStepChanged({ newStep: 'Step1', newStepRuName: 'Оценка товара' });
  }, [onStepChanged]);

  React.useEffect(() => {
    if (postReviewResultStatus === 'ok') {
      onStepChanged({ newStep: 'Step10', newStepRuName: 'Спасибо' });
    }
  }, [postReviewResultStatus, onStepChanged]);

  const doFormSubmit = React.useCallback(
    (formState: Reducers.FormState) => {
      const dataFromFormState = mapFromStateToPostData(formState, productId ?? '');

      onSubmit?.({
        ...dataFromFormState,
        photo_ids: selectedPhotos.flatMap((item) => {
          return item.uploadedPhotoId != null ? item.uploadedPhotoId : [];
        }),
      });
    },
    [productId, onSubmit, selectedPhotos],
  );

  const takeUploadToken = React.useMemo(() => uploadTokenSelector(selectedPhotos), [selectedPhotos]);

  const doRetryPhotoUpload = React.useCallback(
    (coverSrc: string) => {
      const uploadToken = takeUploadToken(coverSrc);
      if (uploadToken !== null) {
        onRetryPhotoUpload?.(uploadToken);
      }
    },
    [takeUploadToken, onRetryPhotoUpload],
  );

  const doRemovePhoto = React.useCallback(
    (coverSrc: string) => {
      const uploadToken = takeUploadToken(coverSrc);
      if (uploadToken !== null) {
        onRemovePhoto?.(uploadToken);
      }
    },
    [takeUploadToken, onRemovePhoto],
  );

  const selectedPictures = React.useMemo(
    () =>
      selectedPhotos.map((r) => ({
        src: r.coverSrc,
        isInProcessing: r.isInProcessing,
        processingIsFailed: r.processingIsFailed,
      })),
    [selectedPhotos],
  );

  return (
    <>
      {postReviewResultStatus !== 'ok' && (
        <ReviewFormStatefull
          onSubmit={doFormSubmit}
          submitIsPerforming={postReviewIsPerforming}
          breakpoint={breakpoint}
          productTile={<ProductCardWrapper />}
          selectedPictures={selectedPictures}
          onPictureRetryClick={doRetryPhotoUpload}
          onPictureRemoveClick={doRemovePhoto}
          onPictureSelectionClick={onPhotoSelectionClick}
          onPicturesFilesSelect={onPhotosSelect}
          onReviewPublicationErrorShown={onReviewPublicationErrorShown}
          {...restProps}
        />
      )}
      {postReviewResultStatus === 'ok' && <FormSubmitThanksSection productMainPhoto={productMainPhoto} />}
    </>
  );
};
export const mapStoreToProps = (state: RootReducer): ReviewFormConnectedStateProps => {
  const primPrice = getPrimaryPrice(state);
  const media = getProductImages(state);
  return {
    availabledStores: getAvailabledStores(state),
    availabledRegions: getAvailabledRegions(state),
    productTitle: getDisplayedName(state) ?? '',
    productMainPhoto: media?.[0].url ?? '',
    productPrice: {
      value: primPrice?.price ?? 0,
      currency: primPrice?.currency ?? '',
    },
    productId: getProductId(state),
    postSubmitErrors: getPostReviewErrors(state),
    postReviewIsPerforming: getPostReviewIsPerforming(state) ?? false,
    postReviewResultStatus: getPostReviewResultStatus(state),
    selectedPhotos: getSelectedPhotos(state),
  };
};
export const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSubmit: (postData: FetchModels.PostReviewData) => dispatch(POST_REVIEW.request(postData)),
  onPhotoSelectionClick: () => dispatch(ReviewFormActions.PHOTO_SELECTION_CLICK()),
  onPhotosSelect: (photos: File[]) => dispatch(PROCESS_SELECTED_PHOTOS(photos)),
  onRemovePhoto: (uploadToken: UploadPhotoToken) => dispatch(REMOVE_SELECTED_PHOTO(uploadToken)),
  onRetryPhotoUpload: (uploadToken: UploadPhotoToken) => dispatch(PERFORM_UPLOAD_RETRY(uploadToken)),
  onStepChanged: (params: { newStep: Step; newStepRuName: string }) =>
    dispatch(PDP_REVIEW_FORM_STEP_CHANGED(params)),
  onReviewPublicationErrorShown: (params: { ruText: string }) =>
    dispatch(PDP_REVIEW_FORM_PUBLICATION_ERROR_SHOWN(params)),
  dispatch,
  onBodyFieldFocus: () => dispatch(ReviewFormActions.BODY_FIELD_FOCUSED()),
  onProsFieldFocus: () => dispatch(ReviewFormActions.PROS_FIELD_FOCUSED()),
  onConsFieldFocus: () => dispatch(ReviewFormActions.CONS_FIELD_FOCUSED()),
  setShop: (region: string | null, shop: string | null) => {
    if (region) {
      dispatch(ReviewFormActions.AUTHOR_REGION_SELECTED({ regionName: region }));
    }
    if (shop) {
      dispatch(ReviewFormActions.AUTHOR_SHOP_SELECTED({ shopName: shop }));
    }
  },
  onAuthorNameFieldFocus: () => dispatch(ReviewFormActions.AUTHOR_NAME_FIELD_FOCUSED()),
  onAuthorEmailFieldFocus: () => dispatch(ReviewFormActions.AUTHOR_EMAIL_FIELD_FOCUSED()),
});
export const PdpReviewForm = connect(mapStoreToProps, mapDispatchToProps)(ReviewFormConnected);
